import {
  type FormEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { errorMessage } from '@/shared/api/errors';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { Card } from '@/shared/ui/Card';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Segmented, type SegmentOption } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import {
  AlertIcon,
  DownloadIcon,
  MoreIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UploadIcon,
} from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { DOCUMENTS_PAGE, type DocumentListParams, type DocumentView, exportUrl } from './api';
import { type Document, type DocumentKind, KINDS, kindLabel, monthLabel } from './types';
import {
  useDocumentList,
  useDocumentMonths,
  useOpenDocument,
  useReplaceDocument,
  useRestoreDocument,
  useTrashDocument,
  useUploadDocument,
} from './useDocuments';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });

/** The same list the back accepts (`extraction.documents.ACCEPTED_EXTENSIONS`). */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.txt,.xlsx,.xlsm,.xls,.csv,.tsv,.docx';

const VIEW_OPTIONS: SegmentOption<DocumentView>[] = [
  { value: 'live', label: 'Archivo' },
  { value: 'versions', label: 'Con versiones' },
  { value: 'trash', label: 'Papelera' },
];

/** The month as a chip reads it: «sept 2026». The full name stays in the tooltip. */
const MONTH_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sept',
  'oct',
  'nov',
  'dic',
] as const;

function shortMonthLabel(year: number, month: number): string {
  return `${MONTH_SHORT[month - 1] ?? month} ${year}`;
}

const HELP = [
  <>
    Cada adjunto de un correo clasificado y cada presupuesto aprobado <strong>entran solos</strong>,
    con su tipo. Si el tipo no es el bueno, corrígelo desde el correo o aquí.
  </>,
  <>
    Facturas, albaranes y presupuestos emitidos se conservan <strong>seis años por ley</strong>: la
    etiqueta «6 años» lo marca, y la fecha hasta la que se guarda sale al pasar el ratón.
  </>,
  <>
    Borrar pide un motivo y manda a la papelera treinta días; después desaparece. Sustituir guarda
    la versión anterior: se ve en «Con versiones».
  </>,
  <>Con un mes elegido, «Exportar el mes en ZIP» descarga todo lo de ese mes.</>,
];

type Period = { year: number; month: number };

/**
 * The document archive (contract v10.5): what the shop keeps beyond the mail. Browsed by
 * month, searched by kind, counterparty, number and text; uploads by hand in a modal; a
 * confirmed trash; the ZIP of a month. What enters on its own (attachments, issued quotes) and how
 * long each kind is kept is the back's business (`docs/documentos.md`); this screen shows
 * it and lets a person correct it.
 *
 * Content first: the filters are one row, the months one row of chips under them, and
 * the list starts right there. The upload form is a sheet over it, only while somebody
 * asked for it.
 */
export function DocumentsPage() {
  const [view, setView] = useState<DocumentView>('live');
  const [kind, setKind] = useState<DocumentKind | ''>('');
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState<Period | null>(null);
  const [offset, setOffset] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<Document | null>(null);

  const params: DocumentListParams = {
    view,
    kind: kind || undefined,
    q: q.trim() || undefined,
    year: period?.year,
    month: period?.month,
    limit: DOCUMENTS_PAGE,
    offset,
  };
  const documents = useDocumentList(params);
  const months = useDocumentMonths();
  const total = documents.data?.total ?? 0;
  const lastOnPage = Math.min(offset + DOCUMENTS_PAGE, total);
  const items = documents.data?.items ?? [];
  // An empty archive with nothing narrowing it is a different message from an empty
  // result: «prueba con otro mes» is bad advice when there is no month to try.
  const filtered = view !== 'live' || kind !== '' || q.trim() !== '' || period !== null;

  return (
    <>
      <PageHeader
        title="Archivo"
        help={HELP}
        actions={
          <Button icon={<PlusIcon size={18} />} onClick={() => setUploading(true)}>
            Subir documento
          </Button>
        }
      />

      {uploading ? (
        <UploadForm
          onClose={() => setUploading(false)}
          onDone={(created) => {
            // Back to the list: the row is in it already, and the next upload starts clean.
            setUploaded(created);
            setUploading(false);
          }}
        />
      ) : null}

      {uploaded ? (
        <Callout
          tone="ok"
          compact
          role="status"
          action={
            <Button variant="ghost" size="sm" onClick={() => setUploaded(null)}>
              Cerrar
            </Button>
          }
        >
          Archivado: {uploaded.filename} como {kindLabel(uploaded.kind).toLowerCase()}.
        </Callout>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="Vista"
          value={view}
          options={VIEW_OPTIONS}
          onChange={(value) => {
            setView(value);
            setOffset(0);
          }}
        />
        <select
          aria-label="Tipo"
          className="field w-full sm:w-52"
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as DocumentKind | '');
            setOffset(0);
          }}
        >
          <option value="">Todos los tipos</option>
          {KINDS.map((each) => (
            <option key={each} value={each}>
              {kindLabel(each)}
            </option>
          ))}
        </select>
        <div className="relative w-full min-w-[14rem] flex-1 sm:max-w-sm">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar"
            placeholder="Nombre, contraparte, número…"
            className="field w-full pl-9"
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setOffset(0);
            }}
          />
        </div>
      </div>

      <MonthChips
        months={months.data ?? []}
        selected={period}
        onSelect={(value) => {
          setPeriod(value);
          setOffset(0);
        }}
      />

      {documents.isPending ? <Spinner variant="block" label="Cargando el archivo" /> : null}
      {documents.isError ? (
        <ErrorMessage error={documents.error}>No se pudieron cargar los documentos</ErrorMessage>
      ) : null}
      {documents.data && items.length === 0 ? (
        <EmptyState
          title={
            view === 'trash'
              ? 'La papelera está vacía'
              : filtered
                ? 'Nada con esos filtros'
                : 'Todavía no hay documentos'
          }
          hint={
            view === 'trash'
              ? 'Lo que se borra pasa treinta días aquí antes de desaparecer.'
              : filtered
                ? 'Prueba con otro mes, otro tipo o sin texto en la búsqueda.'
                : 'Lo que llega por correo se archiva solo; lo demás se sube con «Subir documento».'
          }
        />
      ) : null}

      {documents.data && items.length > 0 ? (
        <>
          <div className="hidden md:block">
            <Card padded={false}>
              <Table label="Documentos del archivo" minWidth="0">
                <THead>
                  <Tr>
                    <Th>Documento</Th>
                    <Th>Tipo</Th>
                    <Th>Contraparte</Th>
                    <Th>Número</Th>
                    <Th className="w-28">Fecha</Th>
                    <Th className="w-28" />
                  </Tr>
                </THead>
                <TBody>
                  {items.map((document) => (
                    <DocumentRow key={document.id} document={document} view={view} />
                  ))}
                </TBody>
              </Table>
              {total > DOCUMENTS_PAGE ? (
                <Pagination
                  offset={offset}
                  lastOnPage={lastOnPage}
                  total={total}
                  onChange={setOffset}
                />
              ) : null}
            </Card>
          </div>

          <CardList label="Documentos del archivo" className="md:hidden">
            {items.map((document) => (
              <DocumentCard key={document.id} document={document} view={view} />
            ))}
          </CardList>
          {total > DOCUMENTS_PAGE ? (
            <div className="md:hidden">
              <Pagination
                offset={offset}
                lastOnPage={lastOnPage}
                total={total}
                onChange={setOffset}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function Pagination({
  offset,
  lastOnPage,
  total,
  onChange,
}: {
  offset: number;
  lastOnPage: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  return (
    <footer className="border-line-2 text-ink-3 flex items-center justify-end gap-3 border-t px-4 py-2 text-[13px]">
      <span>
        {offset + 1}–{lastOnPage} de {total}
      </span>
      <Button
        size="sm"
        variant="secondary"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - DOCUMENTS_PAGE))}
      >
        Anteriores
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={lastOnPage >= total}
        onClick={() => onChange(offset + DOCUMENTS_PAGE)}
      >
        Siguientes
      </Button>
    </footer>
  );
}

/**
 * The months as a row of chips: «Todo · sept 2026 · ago 2026 …», newest first as the API
 * sends them, scrolling sideways when there are many. The ZIP of the chosen month sits
 * right after them, a secondary action: it is what the accountant asks for once a month.
 */
function MonthChips({
  months,
  selected,
  onSelect,
}: {
  months: { year: number; month: number; count: number; size_bytes: number }[];
  selected: Period | null;
  onSelect: (value: Period | null) => void;
}) {
  return (
    <nav aria-label="Meses del archivo" className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex max-w-full gap-1.5 overflow-x-auto">
        <MonthChip active={selected === null} onClick={() => onSelect(null)}>
          Todo
        </MonthChip>
        {months.map((each) => {
          const active = selected?.year === each.year && selected?.month === each.month;
          return (
            <MonthChip
              key={`${each.year}-${each.month}`}
              active={active}
              title={`${monthLabel(each.year, each.month)}: ${each.count} documentos, ${formatBytes(each.size_bytes)}`}
              onClick={() => onSelect({ year: each.year, month: each.month })}
            >
              {shortMonthLabel(each.year, each.month)}
              <span
                className={cn(
                  'text-[11.5px] tabular-nums',
                  active ? 'text-accent-2/70' : 'text-ink-4',
                )}
              >
                {each.count}
              </span>
            </MonthChip>
          );
        })}
        {months.length === 0 ? (
          <span className="text-ink-4 self-center text-[13px]">Todavía no hay nada.</span>
        ) : null}
      </div>
      {selected ? (
        <a
          href={exportUrl(selected.year, selected.month)}
          download
          className="bg-card text-ink ring-line hover:bg-paper-2 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap ring-1 ring-inset"
        >
          <DownloadIcon size={15} />
          Exportar el mes en ZIP
        </a>
      ) : null}
    </nav>
  );
}

function MonthChip({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium whitespace-nowrap ring-1 ring-inset transition-colors',
        'focus-visible:outline-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
        active
          ? 'bg-accent-soft text-accent-2 ring-accent/30'
          : 'bg-card text-ink-2 ring-line hover:bg-paper-2 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The upload, as a sheet over the list: the fields, «Archivar» and «Cancelar», and the
 * list right where it was when it closes. A `<form>` with an id and a submit button in
 * the footer that points to it, so Enter in a field still archives.
 */
function UploadForm({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (created: Document) => void;
}) {
  const upload = useUploadDocument();
  const formId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<DocumentKind>('factura_proveedor');
  const [counterparty, setCounterparty] = useState('');
  const [reference, setReference] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [sensitive, setSensitive] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    upload.mutate(
      {
        file,
        kind,
        counterparty: counterparty.trim() || undefined,
        reference: reference.trim() || undefined,
        document_date: documentDate || undefined,
        sensitive: sensitive || undefined,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <Dialog
      title="Subir un documento"
      description="Queda archivado con su tipo, y se conserva lo que ese tipo pida."
      onClose={upload.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={upload.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} disabled={!file || upload.isPending}>
            {upload.isPending ? 'Subiendo…' : 'Archivar'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input
            label="Fichero"
            type="file"
            accept={ACCEPT}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </div>
        <Select
          label="Tipo de documento"
          value={kind}
          onChange={(event) => setKind(event.target.value as DocumentKind)}
        >
          {KINDS.map((each) => (
            <option key={each} value={each}>
              {kindLabel(each)}
            </option>
          ))}
        </Select>
        <Input
          label="Fecha del documento"
          optional
          type="date"
          hint="Desde cuándo cuenta la conservación. Sin ella, hoy."
          value={documentDate}
          onChange={(event) => setDocumentDate(event.target.value)}
        />
        <Input
          label="Contraparte"
          optional
          placeholder="Proveedor o cliente"
          value={counterparty}
          onChange={(event) => setCounterparty(event.target.value)}
        />
        <Input
          label="Número"
          optional
          placeholder="Factura, albarán, pedido…"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
        <label className="text-ink-2 flex items-center gap-2 text-[13.5px] sm:col-span-2">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={sensitive}
            onChange={(event) => setSensitive(event.target.checked)}
          />
          Pagos y cobros
        </label>
        {upload.isError ? (
          <p role="alert" className="text-danger text-[13.5px] sm:col-span-2">
            {errorMessage(upload.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/** The badges a document carries, by exception: legal, sensitive, superseded. */
function DocumentBadges({ document, replaced }: { document: Document; replaced: boolean }) {
  return (
    <>
      {document.legal ? (
        <Badge
          tone="info"
          title={`La ley obliga a conservarlo hasta el ${formatDate(dateFormat, document.retain_until)}.`}
        >
          6 años
        </Badge>
      ) : null}
      {document.sensitive ? <Badge tone="warn">pagos y cobros</Badge> : null}
      {replaced ? <Badge tone="neutral">versión anterior</Badge> : null}
    </>
  );
}

function TrashLine({ document }: { document: Document }) {
  if (!document.deleted_at) return null;
  return (
    <span className="text-ink-4 block text-[12px]">
      En la papelera desde {formatDate(dateFormat, document.deleted_at)}
      {document.delete_reason ? `: ${document.delete_reason}` : ''}
    </span>
  );
}

function DocumentRow({ document, view }: { document: Document; view: DocumentView }) {
  const inTrash = document.deleted_at !== null;
  const replaced = document.replaced_by_id !== null;

  return (
    <Tr className={cn(inTrash || replaced ? 'text-ink-3' : '')}>
      <Td>
        <span className={cn('font-medium', inTrash || replaced ? 'text-ink-3' : 'text-ink')}>
          {document.filename}
        </span>
        <span className="text-ink-4 ml-2 text-[12px]">{formatBytes(document.size_bytes)}</span>
        <TrashLine document={document} />
      </Td>
      <Td>
        <span className="flex flex-wrap items-center gap-1">
          {kindLabel(document.kind)}
          <DocumentBadges document={document} replaced={replaced} />
        </span>
      </Td>
      <Td>{document.counterparty ?? ''}</Td>
      <Td className="whitespace-nowrap">{document.reference ?? ''}</Td>
      <Td className="whitespace-nowrap tabular-nums">
        {document.document_date ? formatDate(dateFormat, document.document_date) : ''}
      </Td>
      <Td className="text-right whitespace-nowrap">
        <DocumentActions document={document} view={view} />
      </Td>
    </Tr>
  );
}

function DocumentCard({ document, view }: { document: Document; view: DocumentView }) {
  const inTrash = document.deleted_at !== null;
  const replaced = document.replaced_by_id !== null;
  const badged = document.legal || document.sensitive || replaced;
  const subtitle = [kindLabel(document.kind), document.counterparty, document.reference]
    .filter(Boolean)
    .join(' · ');
  return (
    <CardListItem
      title={document.filename}
      subtitle={subtitle}
      aside={badged ? <DocumentBadges document={document} replaced={replaced} /> : undefined}
      footer={
        <>
          <span className="tabular-nums">
            {document.document_date ? formatDate(dateFormat, document.document_date) : ''}
            <span className="text-ink-4 ml-2">{formatBytes(document.size_bytes)}</span>
          </span>
          <DocumentActions document={document} view={view} />
        </>
      }
    >
      {inTrash ? <TrashLine document={document} /> : null}
    </CardListItem>
  );
}

/** What the API asks of a reason (`DocumentDelete.reason`): three characters at least. */
const REASON_MIN = 3;

/**
 * The question before the trash: which document, what happens to it, and a line for the
 * reason, which the API records next to the row and the trash shows («En la papelera
 * desde …: duplicada»). Enter in the field borrows; the API's refusal (a document the
 * law says to keep) lands inside the sheet, so the person reads it and gives up.
 */
function TrashDialog({
  document,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  document: Document;
  busy: boolean;
  error: unknown;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const formId = useId();
  const [reason, setReason] = useState('');
  const ready = reason.trim().length >= REASON_MIN;

  return (
    <Dialog
      title="¿Borrar este documento?"
      size="sm"
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} variant="danger" disabled={busy || !ready}>
            {busy ? 'Borrando…' : 'Borrar'}
          </Button>
        </>
      }
    >
      <p className="text-ink-2 text-[14.5px] leading-relaxed">
        <strong className="text-ink">{document.filename}</strong> pasa a la papelera. Se puede
        recuperar desde allí durante treinta días; después desaparece.
      </p>
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) onConfirm(reason.trim());
        }}
      >
        <Input
          label="Motivo"
          placeholder="Duplicado, equivocado de tipo, ya no hace falta…"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          minLength={REASON_MIN}
          maxLength={500}
          required
        />
      </form>
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger border-danger/20 flex items-start gap-2 rounded-md border px-3 py-2 text-[13.5px]"
        >
          <AlertIcon size={16} className="mt-0.5 shrink-0" />
          <span>{errorMessage(error)}</span>
        </p>
      ) : null}
    </Dialog>
  );
}

/**
 * What can be done with a document: open it, and behind «⋯» replace it or trash it; in
 * the trash, restore it. Trashing asks for a reason in a modal (`TrashDialog`); the API's
 * refusal for a document under a legal duty lands inside that modal.
 */
function DocumentActions({ document, view }: { document: Document; view: DocumentView }) {
  const open = useOpenDocument();
  const trash = useTrashDocument();
  const restore = useRestoreDocument();
  const replace = useReplaceDocument();
  const fileInput = useRef<HTMLInputElement>(null);
  const [confirming, setConfirming] = useState(false);

  const inTrash = document.deleted_at !== null;
  const replaced = document.replaced_by_id !== null;
  const readOnly = view === 'versions' && replaced;

  function closeConfirm() {
    setConfirming(false);
    trash.reset();
  }

  return (
    <div className="inline-flex flex-col items-end gap-1">
      {confirming ? (
        <TrashDialog
          document={document}
          busy={trash.isPending}
          error={trash.isError ? trash.error : undefined}
          onCancel={closeConfirm}
          onConfirm={(reason) =>
            trash.mutate({ id: document.id, reason }, { onSuccess: () => setConfirming(false) })
          }
        />
      ) : null}
      <div className="inline-flex flex-wrap items-center justify-end gap-1">
        {inTrash ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={restore.isPending}
            onClick={() => restore.mutate(document.id)}
          >
            Recuperar
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={open.mutation.isPending}
              onClick={() => open.mutation.mutate({ id: document.id, inline: true })}
              title="Abrir en una pestaña nueva"
            >
              Abrir
            </Button>
            {open.blockedLink ? (
              <a
                href={open.blockedLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand text-[12.5px] underline"
              >
                abrir
              </a>
            ) : null}
            {readOnly ? null : (
              <RowMenu label={`Más acciones para ${document.filename}`}>
                <MenuItem
                  icon={<UploadIcon size={16} />}
                  onClick={() => fileInput.current?.click()}
                  disabled={replace.isPending}
                >
                  {replace.isPending ? 'Subiendo…' : 'Sustituir'}
                </MenuItem>
                <MenuItem
                  icon={<TrashIcon size={16} />}
                  tone="danger"
                  onClick={() => setConfirming(true)}
                >
                  Borrar
                </MenuItem>
              </RowMenu>
            )}
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) replace.mutate({ id: document.id, file });
                event.target.value = '';
              }}
            />
          </>
        )}
      </div>
      {replace.isError ? (
        <span role="alert" className="text-danger text-right text-[12.5px]">
          {errorMessage(replace.error)}
        </span>
      ) : null}
      {open.mutation.isError ? (
        <span role="alert" className="text-danger text-right text-[12.5px]">
          {errorMessage(open.mutation.error)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The «⋯» of a row: a small panel with the actions that are not the main one. Closes
 * with Escape, with a click outside, when the page scrolls, and after choosing something.
 *
 * Painted `fixed` from the button's rectangle, not `absolute` inside the row: the table
 * lives in a scroll region (`overflow-x: auto`), and a panel hanging under the last row
 * would be clipped by it, or grow it a scrollbar.
 */
function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const open = anchor !== null;

  useEffect(() => {
    if (!open) return;
    function close() {
      setAnchor(null);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    function onPointerDown(event: PointerEvent) {
      if (root.current && !root.current.contains(event.target as Node)) close();
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('scroll', close, true);
    };
  }, [open]);

  function toggle(event: MouseEvent<HTMLButtonElement>) {
    if (open) {
      setAnchor(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchor({ top: rect.bottom + 4, right: Math.max(0, window.innerWidth - rect.right) });
  }

  return (
    <div ref={root} className="inline-flex">
      <Button size="sm" variant="ghost" aria-label={label} aria-expanded={open} onClick={toggle}>
        <MoreIcon size={16} />
      </Button>
      {anchor ? (
        <div
          style={{ position: 'fixed', top: anchor.top, right: anchor.right }}
          className="border-line bg-card z-30 flex min-w-[10rem] flex-col rounded-md border p-1 text-left shadow-lg"
          onClick={() => setAnchor(null)}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** One line of the menu: an icon that says what it does, and the word next to it. */
function MenuItem({
  icon,
  tone = 'default',
  onClick,
  disabled = false,
  children,
}: {
  icon: ReactNode;
  tone?: 'default' | 'danger';
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-full items-center gap-2 rounded px-2.5 text-left text-[13.5px] whitespace-nowrap disabled:opacity-50',
        tone === 'danger'
          ? 'text-danger hover:bg-danger-soft'
          : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
      )}
    >
      <span
        className={cn('inline-flex shrink-0', tone === 'danger' ? 'text-danger' : 'text-ink-3')}
        aria-hidden="true"
      >
        {icon}
      </span>
      {children}
    </button>
  );
}
